import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap, switchMap } from 'rxjs';
import { ModuleService, InverterService, StorageService } from '@features/catalog/services';
import { SolarModule, SolarInverter, SolarStorage } from '@features/catalog/models';
import { ToastService } from '@shared/services/toast.service';

/**
 * Equipment Store State
 * Manages all equipment types (modules, inverters, storages) in one unified store
 */
interface EquipmentState {
  // Modules
  modules: SolarModule[];

  // Inverters
  inverters: SolarInverter[];

  // Storages
  storages: SolarStorage[];

  // Common state
  loading: boolean;
  error: string | null;
  lastOrganizationId: string | null;
}

/**
 * Unified Equipment Store
 * Handles state management for all equipment types (modules, inverters, storages)
 */
export const EquipmentStore = signalStore(
  { providedIn: 'root' },
  withState<EquipmentState>({
    modules: [],
    inverters: [],
    storages: [],
    loading: false,
    error: null,
    lastOrganizationId: null,
  }),
  withComputed((state) => ({
    // Modules
    allModules: computed(() => state.modules()),
    moduleCount: computed(() => state.modules().length),

    // Inverters
    allInverters: computed(() => state.inverters()),
    inverterCount: computed(() => state.inverters().length),

    // Storages
    allStorages: computed(() => state.storages()),
    storageCount: computed(() => state.storages().length),

    // Common
    isLoading: computed(() => state.loading()),
    hasError: computed(() => !!state.error()),
  })),
  withMethods(
    (
      store,
      moduleService = inject(ModuleService),
      inverterService = inject(InverterService),
      storageService = inject(StorageService),
      toastService = inject(ToastService),
    ) => ({
      // ===== MODULES =====
      loadModules: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          switchMap((organizationId) =>
            moduleService.getModules(organizationId).then(
              (response) => {
                let modules = response.data;
                // Calculate missing fields
                modules = modules.map((m) => ({
                  ...m,
                  totalCosts:
                    m.costs + m.installationCosts + m.mountingSystemCosts,
                  areaM2: (m.height * m.width) / 10000,
                }));
                patchState(store, {
                  modules,
                  loading: false,
                  lastOrganizationId: organizationId,
                });
                return { success: true };
              },
              (error) => {
                patchState(store, {
                  loading: false,
                  error: error.message || 'Fehler beim Laden der Module',
                });
                toastService.error('Fehler beim Laden der Module');
                return { success: false };
              },
            ),
          ),
        ),
      ),

      async createModule(organizationId: string, data: any) {
        patchState(store, { loading: true, error: null });
        try {
          let newModule = await moduleService.createModule(
            organizationId,
            data,
          );
          // Calculate missing fields
          newModule = {
            ...newModule,
            totalCosts:
              newModule.costs +
              newModule.installationCosts +
              newModule.mountingSystemCosts,
            areaM2: (newModule.height * newModule.width) / 10000,
          };
          patchState(store, {
            modules: [...store.modules(), newModule],
            loading: false,
          });
          toastService.success('Modul erfolgreich erstellt');
          return newModule;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Erstellen des Moduls',
          });
          toastService.error('Fehler beim Erstellen des Moduls');
          throw error;
        }
      },

      async updateModule(organizationId: string, moduleId: string, data: any) {
        patchState(store, { loading: true, error: null });
        try {
          let updatedModule = await moduleService.updateModule(
            organizationId,
            moduleId,
            data,
          );
          // Calculate missing fields
          updatedModule = {
            ...updatedModule,
            totalCosts:
              updatedModule.costs +
              updatedModule.installationCosts +
              updatedModule.mountingSystemCosts,
            areaM2: (updatedModule.height * updatedModule.width) / 10000,
          };
          patchState(store, {
            modules: store
              .modules()
              .map((m) => (m.id === moduleId ? updatedModule : m)),
            loading: false,
          });
          toastService.success('Modul erfolgreich aktualisiert');
          return updatedModule;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Aktualisieren des Moduls',
          });
          toastService.error('Fehler beim Aktualisieren des Moduls');
          throw error;
        }
      },

      async deleteModule(organizationId: string, moduleId: string) {
        patchState(store, { loading: true, error: null });
        try {
          await moduleService.deleteModule(organizationId, moduleId);
          patchState(store, {
            modules: store.modules().filter((m) => m.id !== moduleId),
            loading: false,
          });
          toastService.success('Modul erfolgreich gelöscht');
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Löschen des Moduls',
          });
          toastService.error('Fehler beim Löschen des Moduls');
          throw error;
        }
      },

      async syncModules(organizationId: string) {
        patchState(store, { loading: true, error: null });
        try {
          const result = await moduleService.syncGlobalModules(organizationId);
          // Reload modules after sync
          const response = await moduleService.getModules(organizationId);
          let modules = response.data;
          // Calculate missing fields
          modules = modules.map((m) => ({
            ...m,
            totalCosts: m.costs + m.installationCosts + m.mountingSystemCosts,
            areaM2: (m.height * m.width) / 10000,
          }));
          patchState(store, {
            modules,
            loading: false,
          });
          toastService.success(
            `${result.synced} Module synchronisiert, ${result.failed} fehlgeschlagen`,
          );
          return result;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Synchronisieren der Module',
          });
          toastService.error('Fehler beim Synchronisieren der Module');
          throw error;
        }
      },

      // ===== INVERTERS =====
      loadInverters: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          switchMap((organizationId) =>
            inverterService.getInverters(organizationId).then(
              (response) => {
                patchState(store, {
                  inverters: response.data,
                  loading: false,
                  lastOrganizationId: organizationId,
                });
                return { success: true };
              },
              (error) => {
                patchState(store, {
                  loading: false,
                  error:
                    error.message || 'Fehler beim Laden der Wechselrichter',
                });
                toastService.error('Fehler beim Laden der Wechselrichter');
                return { success: false };
              },
            ),
          ),
        ),
      ),

      async createInverter(organizationId: string, data: any) {
        patchState(store, { loading: true, error: null });
        try {
          const newInverter = await inverterService.createInverter(
            organizationId,
            data,
          );
          patchState(store, {
            inverters: [...store.inverters(), newInverter],
            loading: false,
          });
          toastService.success('Wechselrichter erfolgreich erstellt');
          return newInverter;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Erstellen des Wechselrichters',
          });
          toastService.error('Fehler beim Erstellen des Wechselrichters');
          throw error;
        }
      },

      async updateInverter(
        organizationId: string,
        inverterId: string,
        data: any,
      ) {
        patchState(store, { loading: true, error: null });
        try {
          const updatedInverter = await inverterService.updateInverter(
            organizationId,
            inverterId,
            data,
          );
          patchState(store, {
            inverters: store
              .inverters()
              .map((i) => (i.id === inverterId ? updatedInverter : i)),
            loading: false,
          });
          toastService.success('Wechselrichter erfolgreich aktualisiert');
          return updatedInverter;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error:
              error.message || 'Fehler beim Aktualisieren des Wechselrichters',
          });
          toastService.error('Fehler beim Aktualisieren des Wechselrichters');
          throw error;
        }
      },

      async deleteInverter(organizationId: string, inverterId: string) {
        patchState(store, { loading: true, error: null });
        try {
          await inverterService.deleteInverter(organizationId, inverterId);
          patchState(store, {
            inverters: store.inverters().filter((i) => i.id !== inverterId),
            loading: false,
          });
          toastService.success('Wechselrichter erfolgreich gelöscht');
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Löschen des Wechselrichters',
          });
          toastService.error('Fehler beim Löschen des Wechselrichters');
          throw error;
        }
      },

      async syncInverters(organizationId: string) {
        patchState(store, { loading: true, error: null });
        try {
          const result =
            await inverterService.syncGlobalInverters(organizationId);
          // Reload inverters after sync
          const response = await inverterService.getInverters(organizationId);
          patchState(store, {
            inverters: response.data,
            loading: false,
          });
          toastService.success(
            `${result.length} Wechselrichter synchronisiert`,
          );
          return result;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error:
              error.message || 'Fehler beim Synchronisieren der Wechselrichter',
          });
          toastService.error('Fehler beim Synchronisieren der Wechselrichter');
          throw error;
        }
      },

      // ===== STORAGES =====
      loadStorages: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          switchMap((organizationId) =>
            storageService.getStorages(organizationId).then(
              (response) => {
                patchState(store, {
                  storages: response.data,
                  loading: false,
                  lastOrganizationId: organizationId,
                });
                return { success: true };
              },
              (error) => {
                patchState(store, {
                  loading: false,
                  error: error.message || 'Fehler beim Laden der Speicher',
                });
                toastService.error('Fehler beim Laden der Speicher');
                return { success: false };
              },
            ),
          ),
        ),
      ),

      async createStorage(organizationId: string, data: any) {
        patchState(store, { loading: true, error: null });
        try {
          const newStorage = await storageService.createStorage(
            organizationId,
            data,
          );
          patchState(store, {
            storages: [...store.storages(), newStorage],
            loading: false,
          });
          toastService.success('Speicher erfolgreich erstellt');
          return newStorage;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Erstellen des Speichers',
          });
          toastService.error('Fehler beim Erstellen des Speichers');
          throw error;
        }
      },

      async updateStorage(
        organizationId: string,
        storageId: string,
        data: any,
      ) {
        patchState(store, { loading: true, error: null });
        try {
          const updatedStorage = await storageService.updateStorage(
            organizationId,
            storageId,
            data,
          );
          patchState(store, {
            storages: store
              .storages()
              .map((s) => (s.id === storageId ? updatedStorage : s)),
            loading: false,
          });
          toastService.success('Speicher erfolgreich aktualisiert');
          return updatedStorage;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Aktualisieren des Speichers',
          });
          toastService.error('Fehler beim Aktualisieren des Speichers');
          throw error;
        }
      },

      async deleteStorage(organizationId: string, storageId: string) {
        patchState(store, { loading: true, error: null });
        try {
          await storageService.deleteStorage(organizationId, storageId);
          patchState(store, {
            storages: store.storages().filter((s) => s.id !== storageId),
            loading: false,
          });
          toastService.success('Speicher erfolgreich gelöscht');
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Löschen des Speichers',
          });
          toastService.error('Fehler beim Löschen des Speichers');
          throw error;
        }
      },

      async syncStorages(organizationId: string) {
        patchState(store, { loading: true, error: null });
        try {
          const result =
            await storageService.syncGlobalStorages(organizationId);
          // Reload storages after sync
          const response = await storageService.getStorages(organizationId);
          patchState(store, {
            storages: response.data,
            loading: false,
          });
          toastService.success(`${result.length} Speicher synchronisiert`);
          return result;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Synchronisieren der Speicher',
          });
          toastService.error('Fehler beim Synchronisieren der Speicher');
          throw error;
        }
      },

      // ===== COMMON =====
      clearError() {
        patchState(store, { error: null });
      },

      /**
       * Load all equipment types at once for an organization
       */
      async loadAllEquipment(organizationId: string) {
        patchState(store, { loading: true, error: null });
        try {
          const [modulesResponse, invertersResponse, storagesResponse] = await Promise.all([
            moduleService.getModules(organizationId),
            inverterService.getInverters(organizationId),
            storageService.getStorages(organizationId),
          ]);

          // Calculate missing fields for modules
          const modulesList = modulesResponse.data.map((m) => ({
            ...m,
            totalCosts: m.costs + m.installationCosts + m.mountingSystemCosts,
            areaM2: (m.height * m.width) / 10000,
          }));

          patchState(store, {
            modules: modulesList,
            inverters: invertersResponse.data,
            storages: storagesResponse.data,
            loading: false,
            lastOrganizationId: organizationId,
          });
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Laden der Ausrüstung',
          });
          toastService.error('Fehler beim Laden der Ausrüstung');
          throw error;
        }
      },
    }),
  ),
);
